'use client';

import { VStack, HStack, Box, Divider, token } from '@terra/ui-styled-system';
import { Text } from '../../../../../components/styled/text';
import { Badge } from '../../../../../components/badge';
import { Card } from '../../../../../components/card';
import { BadgeHttp } from '../../../../../components/styled/badge-http';
import {
  BadgeSeverity,
  type BadgeSeverityType,
} from '../../../../../components/styled/badge-severity';
import { Tooltip } from '../../../../../components/tooltip';
import {
  ChartHistogramIcon,
  File01Icon,
  Idea01Icon,
  SquareLock01Icon,
  Message01Icon,
  Shield01Icon,
  Target03Icon,
  SquareUnlock01Icon,
} from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { MarkdownPreview } from '../../../../markdown-preview';
import { formatCategoryForDisplay } from '../../../../../utils/category-utils';
import type { Signal } from './types';
import type { OnOpenChatObject } from '../../hitl-types';
import { SignalStatusBadge } from '../../../../signals';

type SignalCardProps = {
  signal: Signal;
  variant?: 'compact' | 'detailed';
  onOpenObject?: OnOpenChatObject;
};
const SIGNAL_OBJECT_LABEL = 'Signal';
const SEVERITY_BADGE_TYPES: Record<string, BadgeSeverityType> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};
const resolveSeverityType = (
  severity?: string,
): BadgeSeverityType | undefined => {
  if (!severity) return undefined;

  return SEVERITY_BADGE_TYPES[severity.toLowerCase()];
};

function SeverityBadge({ severity }: { severity: string }) {
  const severityType = resolveSeverityType(severity);

  if (severityType) {
    return (
      <BadgeSeverity size="sm" severity={severityType}>
        {severity}
      </BadgeSeverity>
    );
  }

  return (
    <Badge size="sm" variant="emphasis">
      {severity}
    </Badge>
  );
}

function DetailedFieldSection({
  icon,
  label,
  value,
  isMarkdown = false,
}: {
  icon: React.ReactNode;
  label: string;
  value?: string;
  isMarkdown?: boolean;
}) {
  if (!value) return null;

  return (
    <VStack alignItems="start" gap={1} width="100%">
      <HStack gap={1} alignItems="center" color="fg.muted">
        {icon}
        <Text fontSize="xs" fontWeight="semibold">
          {label}
        </Text>
      </HStack>
      <Box
        width="100%"
        pl={4}
        borderLeftWidth="2px"
        borderLeftColor="border.subtle"
      >
        {isMarkdown ? (
          <Box p={2} borderRadius="sm" width="100%">
            <MarkdownPreview source={value} compact />
          </Box>
        ) : (
          <Text
            fontSize="xs"
            color="fg.default"
            whiteSpace="pre-wrap"
            lineHeight="1.5"
          >
            {value}
          </Text>
        )}
      </Box>
    </VStack>
  );
}

export function SignalCard({
  signal,
  variant = 'compact',
  onOpenObject,
}: SignalCardProps) {
  const t = useTranslations('designSystem.signals.drawer');
  const tCard = useTranslations('designSystem.chat.signalCard');
  const isDetailed = variant === 'detailed';
  const isAppWide = signal.isAppWide === true;
  const displayTitle = signal.title || signal.signalTitle || '';
  const signalObjectId = signal.signalId || signal.id;
  const operationLabel =
    signal.operation === 'update' ? 'Update Signal' : 'Create Signal';
  const hasDetailedContent =
    signal.hypothesis || signal.description || signal.analysis || signal.reason;

  if (!isDetailed) {
    return <CompactSignalCard signal={signal} onOpenObject={onOpenObject} />;
  }

  const canOpenSignal = Boolean(signalObjectId && onOpenObject);
  const handleOpenSignal = () => {
    if (!signalObjectId || !onOpenObject) return;
    onOpenObject({
      type: 'signal',
      id: signalObjectId,
      label: displayTitle,
    });
  };

  return (
    <Card.Root
      flexShrink={0}
      cursor={canOpenSignal ? 'pointer' : 'default'}
      onClick={canOpenSignal ? handleOpenSignal : undefined}
      _hover={canOpenSignal ? { borderColor: 'border.emphasized' } : undefined}
    >
      <Card.Body p={3}>
        <VStack alignItems="start" gap={3} width="100%">
          <HStack
            alignItems="center"
            gap={1.5}
            flexWrap="wrap"
            width="100%"
            justifyContent="space-between"
          >
            <HStack gap={1.5} flexWrap="wrap">
              {isAppWide && (
                <Tooltip
                  title={t('appWideSignalTooltip')}
                  positioning={{ strategy: 'fixed' }}
                >
                  <Badge
                    size="sm"
                    variant="emphasis"
                    bg={token('colors.bg.vulnerability.hot')}
                    borderColor={token('colors.border.vulnerability.hot')}
                  >
                    {t('appWideSignal')}
                  </Badge>
                </Tooltip>
              )}

              {signal.operation ? (
                <Badge size="sm" variant="fill">
                  {operationLabel}
                </Badge>
              ) : null}

              {signal.method && (
                <BadgeHttp size="sm" method={signal.method}>
                  {signal.method}
                </BadgeHttp>
              )}

              {signal.statusCode && (
                <Badge size="sm" variant="outline">
                  {signal.statusCode}
                </Badge>
              )}

              {signal.category && signal.category !== 'Unknown' && (
                <Badge size="sm" variant="outline">
                  {formatCategoryForDisplay(signal.category)}
                </Badge>
              )}

              {signal.severity && <SeverityBadge severity={signal.severity} />}

              {signal.status && <SignalStatusBadge status={signal.status} />}
            </HStack>

            <HStack gap={1.5}>
              {signal.score && (
                <Badge size="sm" variant="emphasis">
                  {signal.score}
                </Badge>
              )}

              {signal.confidence !== undefined && (
                <Badge
                  size="sm"
                  variant="emphasis"
                  icon={<ChartHistogramIcon size={10} />}
                >
                  {`${signal.confidence}%`}
                </Badge>
              )}

              {signal.isAuthenticated !== undefined && (
                <Badge
                  size="sm"
                  variant="emphasis"
                  icon={
                    signal.isAuthenticated ? (
                      <SquareLock01Icon size={10} />
                    ) : (
                      <SquareUnlock01Icon size={10} />
                    )
                  }
                >
                  {signal.isAuthenticated ? 'Auth' : 'Unauth'}
                </Badge>
              )}
            </HStack>
          </HStack>

          {displayTitle && (
            <Text
              fontSize="sm"
              fontWeight="bold"
              color="fg.default"
              lineHeight="1.4"
            >
              {displayTitle}
            </Text>
          )}

          {signal.url && !isAppWide && (
            <Text
              fontSize="xs"
              color="fg.muted"
              fontFamily="mono"
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
              lineHeight="1.5"
            >
              {signal.url}
            </Text>
          )}

          {signal.operation === 'update' && signal.signalId && (
            <HStack gap={1.5} alignItems="center" color="fg.muted">
              <Target03Icon size={12} />
              <Text fontSize="xs" fontFamily="mono">
                {tCard('signalIdLabel', { id: signal.signalId })}
              </Text>
            </HStack>
          )}

          {hasDetailedContent && (
            <>
              <Divider />

              <DetailedFieldSection
                icon={<Idea01Icon size={12} />}
                label="Hypothesis"
                value={signal.hypothesis}
                isMarkdown
              />

              <DetailedFieldSection
                icon={<File01Icon size={12} />}
                label="Description"
                value={signal.description ?? ''}
                isMarkdown
              />

              <DetailedFieldSection
                icon={<Shield01Icon size={12} />}
                label="Technical Analysis"
                value={signal.analysis}
                isMarkdown
              />

              <DetailedFieldSection
                icon={<Message01Icon size={12} />}
                label="Reasoning"
                value={signal.reason}
              />

              {!isAppWide &&
                (signal.endpointId ||
                  signal.harEntryId ||
                  signal.entryPointId) && (
                  <VStack alignItems="start" gap={1} width="100%">
                    <Text fontSize="xs" fontWeight="semibold" color="fg.muted">
                      {tCard('linkedResources')}
                    </Text>
                    <HStack gap={2} flexWrap="wrap">
                      {signal.endpointId && (
                        <Badge size="sm" variant="outline">
                          {tCard('endpointLabel', {
                            id: `${signal.endpointId.slice(0, 8)}...`,
                          })}
                        </Badge>
                      )}
                      {signal.harEntryId && (
                        <Badge size="sm" variant="outline">
                          {tCard('harLabel', {
                            id: `${signal.harEntryId.slice(0, 8)}...`,
                          })}
                        </Badge>
                      )}
                      {signal.entryPointId && (
                        <Badge size="sm" variant="outline">
                          {tCard('entryLabel', {
                            id: `${signal.entryPointId.slice(0, 8)}...`,
                          })}
                        </Badge>
                      )}
                    </HStack>
                  </VStack>
                )}
            </>
          )}
        </VStack>
      </Card.Body>
    </Card.Root>
  );
}

const stripTitlePrefix = (text: string): string =>
  text.replace(/^title:\s*/i, '').trim();
const getCompactRowText = (signal: Signal): string => {
  const title = signal.title || signal.signalTitle;
  const category = signal.category;
  const titleEqualsCategory =
    !!title &&
    !!category &&
    title.trim().toLowerCase() === category.trim().toLowerCase();

  if (title && !titleEqualsCategory) return title;
  const description = signal.description || signal.hypothesis || signal.content;

  if (description) {
    const firstLine = description.split('\n')[0];

    if (firstLine) return stripTitlePrefix(firstLine);
  }

  if (signal.url) return signal.url;

  return title || SIGNAL_OBJECT_LABEL;
};

function CompactSignalCard({
  signal,
  onOpenObject,
}: {
  signal: Signal;
  onOpenObject?: OnOpenChatObject;
}) {
  const rowText = getCompactRowText(signal);
  const signalObjectId = signal.signalId || signal.id;
  const canOpenSignal = Boolean(signalObjectId && onOpenObject);
  const handleOpenSignal = () => {
    if (!signalObjectId || !onOpenObject) return;
    onOpenObject({
      type: 'signal',
      id: signalObjectId,
      label: signal.title || signal.signalTitle || rowText,
    });
  };

  return (
    <HStack
      gap={1.5}
      alignItems="center"
      py={0.5}
      borderRadius="sm"
      cursor={canOpenSignal ? 'pointer' : 'default'}
      _hover={canOpenSignal ? { color: 'fg.default' } : undefined}
      onClick={canOpenSignal ? handleOpenSignal : undefined}
      width="100%"
    >
      {signal.rank !== undefined && (
        <Text
          fontSize="2xs"
          color="fg.subtle"
          fontFamily="mono"
          fontWeight="semibold"
          minW="16px"
          flexShrink={0}
        >
          #{signal.rank}
        </Text>
      )}

      <HStack gap={1} alignItems="center" flex={1} minWidth={0}>
        <Target03Icon size={10} style={{ flexShrink: 0 }} />
        <Text fontSize="xs" color="fg.default" truncate flex={1}>
          {rowText}
        </Text>
      </HStack>

      {signal.category && signal.category !== 'Unknown' && (
        <Badge size="sm" variant="emphasis" flexShrink={0}>
          {formatCategoryForDisplay(signal.category)}
        </Badge>
      )}

      {signal.score !== undefined && (
        <Text fontSize="2xs" color="fg.muted" fontFamily="mono" flexShrink={0}>
          {signal.score}
        </Text>
      )}
    </HStack>
  );
}
